import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Sliders, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../context/AuthContext";
import ClientSelector from "./ClientSelector";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HUMAN_LABELS = {
  profiles: "Profili (Persone & Aziende)",
  intelligence: "Intelligence (OSINT/SOCMINT/HUMINT)",
  tradeshows: "Eventi & Fiere",
  network_events: "Network Events",
  candidates: "Candidati HR",
  patents: "Brevetti",
  technologies: "Tecnologie",
  map_markers: "War Room — Marker",
  marker_details: "War Room — Dettagli",
  marker_documents: "War Room — Documenti",
  normativa: "Normativa",
  training_videos: "Academy — Video",
  course_documents: "Academy — Documenti",
  knowledge_center: "Library — Knowledge",
  reports_docs: "Library — Reports",
  repository: "Library — Repository",
  dashboard_metrics: "Dashboard Metrics",
};

export default function LimitsSettings() {
  const [clientId, setClientId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    axios.get(`${API}/admin/limits${params}`, { withCredentials: true })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error("Load failed: " + formatApiError(e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const updateLocal = (name, value) => {
    setRows((rs) => rs.map((r) => r.collection === name ? { ...r, limit: value } : r));
  };

  const save = async (row) => {
    setSaving(row.collection);
    try {
      const n = parseInt(row.limit, 10);
      await axios.put(`${API}/admin/limits/${row.collection}`,
        { limit: isNaN(n) ? 0 : Math.max(0, n), client_id: clientId || null },
        { withCredentials: true });
      toast.success(`Limite "${HUMAN_LABELS[row.collection] || row.collection}" salvato`);
    } catch (e) {
      toast.error("Save failed: " + formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setSaving(null);
    }
  };

  const reset = async (row) => {
    updateLocal(row.collection, 0);
    setSaving(row.collection);
    try {
      await axios.put(`${API}/admin/limits/${row.collection}`,
        { limit: 0, client_id: clientId || null },
        { withCredentials: true });
      toast.success("Limite rimosso");
    } catch (e) {
      toast.error("Reset failed: " + formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10 max-w-4xl">
      <header className="mb-8">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] mb-2">
          ADMIN · VIEW LIMITS
        </div>
        <h1 className="text-4xl font-black tracking-tight">Impostazioni Limiti</h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Imposta un limite di visualizzazione per ogni sezione del cliente.
          Solo i più recenti N record verranno mostrati nelle viste pubbliche.
          <span className="text-[#00E5FF]"> 0 o vuoto = nessun limite</span> (mostra tutti i dati).
        </p>
      </header>

      <ClientSelector value={clientId} onChange={setClientId} />

      {loading && <div className="text-white/40 text-sm">Loading…</div>}

      <div className="bg-[#0A0E17] border border-white/10 rounded overflow-hidden" data-testid="limits-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 text-[10px] uppercase tracking-widest border-b border-white/10">
              <th className="text-left px-5 py-3">Sezione</th>
              <th className="text-left px-5 py-3 w-48">Limite Visualizzazione</th>
              <th className="text-right px-5 py-3 w-48">Azione</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.collection} data-testid={`limit-row-${row.collection}`} className="border-t border-white/5">
                <td className="px-5 py-3">
                  <div className="text-white/90 font-bold text-sm">
                    {HUMAN_LABELS[row.collection] || row.collection}
                  </div>
                  <div className="text-[10px] text-white/30 uppercase tracking-widest">
                    {row.collection}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Sliders size={14} className="text-[#00E5FF]" />
                    <input
                      type="number"
                      min="0"
                      value={row.limit}
                      onChange={(e) => updateLocal(row.collection, e.target.value)}
                      data-testid={`limit-input-${row.collection}`}
                      className="w-24 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-sm focus:border-[#00E5FF] outline-none"
                    />
                    <span className="text-white/30 text-xs">record</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => reset(row)}
                    disabled={saving === row.collection}
                    data-testid={`reset-${row.collection}`}
                    className="px-2 py-1.5 mr-2 text-xs text-white/50 hover:text-white inline-flex items-center gap-1">
                    <RotateCcw size={12} /> Reset
                  </button>
                  <button onClick={() => save(row)}
                    disabled={saving === row.collection}
                    data-testid={`save-limit-${row.collection}`}
                    className="px-3 py-1.5 bg-[#00E5FF] hover:bg-[#00E5FF]/80 disabled:opacity-40 text-black font-bold rounded text-xs inline-flex items-center gap-1">
                    <Save size={12} />
                    {saving === row.collection ? "..." : "Salva"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
